import { useState, useCallback, useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Button } from './ui/button'
import { useDocumentProperties, useSetDocumentPropertyValue, useUpdateDocumentPropertyValue, useDeleteDocumentPropertyValue, usePropertyDefinitions, usePropertyDefinition, queryKeys } from '../hooks/useQueries'
import { ApiSearchableSelect, type ApiSearchableSelectOption } from './ApiSearchableSelect'
import { AppIcon } from './ui/icon'
import { mdiCheck, mdiClose, mdiDelete, mdiLock, mdiPlus, mdiTag } from '@mdi/js'
import { getBooleanOptions, getPropertyConstraints, EnumPropertyInput } from './properties/PropertyInputUtils'
import { PropertyValueDisplay, PropertyValueEditInput, usesDialogEditor, type PropertySibling } from './properties/PropertyValueCell'
import { useCan } from '../contexts/PermissionsContext'
import { apiClient } from '../api/client'
import { EmptyState } from './ui/empty-state'

interface DocumentPropertiesProps {
    org: string
    documentUid: string
    onPropertyUpdated?: () => void
}

export function DocumentProperties({ org, documentUid, onPropertyUpdated }: DocumentPropertiesProps) {
    const { t, i18n } = useTranslation('documents')
    const { t: tCommon } = useTranslation('common')
    const queryClient = useQueryClient()

    // Properties state
    const [showAddPropertyForm, setShowAddPropertyForm] = useState(false)
    const [newPropertyDefinitionUid, setNewPropertyDefinitionUid] = useState('')
    const [newPropertyValue, setNewPropertyValue] = useState('')
    const [addPropertyError, setAddPropertyError] = useState<string | null>(null)
    const [editingPropertyUid, setEditingPropertyUid] = useState<string | null>(null)
    const [editingPropertyValue, setEditingPropertyValue] = useState('')
    const [propertyEditErrors, setPropertyEditErrors] = useState<Record<string, string>>({})

    // Load document properties
    const { data: properties = [], isLoading: propertiesLoading } = useDocumentProperties(org, documentUid)

    // Extract property definitions from embedded property_definition objects
    const propertyDefinitions = useMemo(() =>
        properties.map(p => (p as any).property_definition).filter(Boolean),
        [properties]
    )

    // Property mutations
    const setPropertyValueMutation = useSetDocumentPropertyValue(org, documentUid)
    const updatePropertyValueMutation = useUpdateDocumentPropertyValue(org, documentUid)
    const deletePropertyValueMutation = useDeleteDocumentPropertyValue(org, documentUid)

    // Helper functions
    const getPropertyDefinition = useCallback((propertyDefinitionUid: string) => {
        return propertyDefinitions.find(pd => pd.uid === propertyDefinitionUid)
    }, [propertyDefinitions])

    const getPropertyDefinitionName = useCallback((propertyDefinitionUid: string) => {
        const propDef = getPropertyDefinition(propertyDefinitionUid)
        if (!propDef) return propertyDefinitionUid

        const locale = i18n.language
        if (typeof propDef.name === 'object' && propDef.name !== null) {
            const names = propDef.name as Record<string, string>
            return names[locale] || names.en || names.fr || Object.values(names)[0] || propDef.code || propertyDefinitionUid
        }

        return propDef.name || propDef.code || propertyDefinitionUid
    }, [getPropertyDefinition, i18n.language])

    // Property definition search for add form - filter for document-specific properties
    const { data: propertyDefinitionsAll = [] } = usePropertyDefinitions(org)
    const propertyDefinitionInitialOptions: ApiSearchableSelectOption[] = useMemo(() => {
        const locale = i18n.language
        return propertyDefinitionsAll
            .filter(pd => {
                // Filter to only show properties that target documents and are not system-managed
                const targetEntityTypes = pd.target_entity_types as string[] | undefined
                return targetEntityTypes && targetEntityTypes.includes('document') && !pd.flags?.includes('system_managed')
            })
            .map(pd => {
                const names = typeof pd.name === 'object' && pd.name !== null ? pd.name as Record<string, string> : null
                const name = names
                    ? names[locale] || names.en || names.fr || Object.values(names)[0] || pd.code || pd.uid
                    : pd.name || pd.code || pd.uid
                // Build label with flags
                const flags = []
                if (pd.data_type === 'enum' && pd.properties?.multiple === true) {
                    flags.push('Multiple')
                }
                if ((pd.data_type === 'boolean' || pd.data_type === 'enum') && pd.flags?.includes('attribute')) {
                    flags.push('Attribute')
                }
                const flagsStr = flags.length > 0 ? ` [${flags.join(', ')}]` : ''
                return {
                    value: pd.uid || '',
                    label: `${name} (${pd.data_type})${flagsStr}`
                }
            })
    }, [propertyDefinitionsAll, i18n.language])

    const searchPropertyDefinitions = useCallback(async (query: string): Promise<ApiSearchableSelectOption[]> => {
        const filtered = propertyDefinitionInitialOptions.filter(option =>
            option.label.toLowerCase().includes(query.toLowerCase())
        )
        return filtered.slice(0, 50)
    }, [propertyDefinitionInitialOptions])

    // Property value display and editing
    const { data: selectedPropertyDef } = usePropertyDefinition(org, newPropertyDefinitionUid)


    // Property editing handlers
    // Editing document properties is `documents.write`; without it the rows
    // render read-only rather than disappearing.
    const canWriteDocuments = useCan('documents.write')

    const startEditingProperty = useCallback((property: any) => {
        setEditingPropertyUid(property.property_uid)
        
        // Format initial value based on property type
        const propDef = propertyDefinitions.find(pd => pd.uid === property.property_uid)
        let initialValue = ''
        
        // Handle enum arrays - convert to comma-separated string for editing
        if (propDef?.data_type === 'enum' && propDef.properties?.multiple === true && Array.isArray(property.value)) {
            initialValue = property.value.join(', ')
        } else if (property.value !== null && property.value !== undefined) {
            initialValue = String(property.value)
        }

        // Format date values for input fields
        if (propDef?.data_type === 'date' && initialValue) {
            try {
                const date = new Date(initialValue)
                if (!isNaN(date.getTime())) {
                    // If with_time is enabled, format as datetime-local, otherwise as date
                    if (propDef.properties?.with_time === true) {
                        // Format for datetime-local input in local timezone (YYYY-MM-DDTHH:mm)
                        const year = date.getFullYear()
                        const month = String(date.getMonth() + 1).padStart(2, '0')
                        const day = String(date.getDate()).padStart(2, '0')
                        const hours = String(date.getHours()).padStart(2, '0')
                        const minutes = String(date.getMinutes()).padStart(2, '0')
                        initialValue = `${year}-${month}-${day}T${hours}:${minutes}`
                    } else {
                        // Format for date input (YYYY-MM-DD) in local timezone
                        const year = date.getFullYear()
                        const month = String(date.getMonth() + 1).padStart(2, '0')
                        const day = String(date.getDate()).padStart(2, '0')
                        initialValue = `${year}-${month}-${day}`
                    }
                }
            } catch {
                // Keep original value if date parsing fails
            }
        }

        setEditingPropertyValue(initialValue)
        setPropertyEditErrors({})
    }, [propertyDefinitions])

    const handleCancelPropertyEdit = useCallback(() => {
        setEditingPropertyUid(null)
        setEditingPropertyValue('')
        setPropertyEditErrors({})
    }, [])

    // `value` is `unknown`, not `string`: object-valued properties (ISO 20022 address, JSON)
    // are saved straight from their dialog editors, so the string-coercion switch below only
    // applies when the editor actually produced a string.
    const handleSavePropertyValue = useCallback(async (propertyUid: string, value: unknown) => {
        try {
            const propDef = propertyDefinitions.find(pd => pd.uid === propertyUid)
            let processedValue: any = value

            // Type coercions only — validation is delegated to the backend
            if (propDef && typeof value === 'string') {
                switch (propDef.data_type) {
                    case 'boolean':
                        processedValue = value === 'true'
                        break
                    case 'date':
                        if (value && !isNaN(Date.parse(value))) {
                            if (propDef.properties?.with_time === true) {
                                const dateValue = new Date(value)
                                processedValue = dateValue.toISOString()
                            } else {
                                const dateValue = new Date(value)
                                processedValue = dateValue.toISOString().split('T')[0]
                            }
                        } else if (value) {
                            throw new Error('Please enter a valid date')
                        }
                        break
                    case 'enum':
                        if (propDef.properties?.multiple === true) {
                            processedValue = value ? value.split(',').map(v => v.trim()).filter(v => v) : []
                        } else {
                            processedValue = value
                        }
                        break
                    case 'number':
                        if (value !== '') {
                            processedValue = parseFloat(value)
                            if (isNaN(processedValue)) {
                                throw new Error('Please enter a valid number')
                            }
                        }
                        break
                    default:
                        processedValue = value
                }
            }

            // Delegate validation to the backend
            let finalValue = processedValue
            try {
                const validation = await apiClient.POST('/api/v1/organizations/{orgCode}/properties/{propertyUid}/validate', {
                    params: { path: { orgCode: org, propertyUid } },
                    body: { value: processedValue }
                })
                if (validation.data && validation.data.status !== 'ok') {
                    setPropertyEditErrors(prev => ({ ...prev, [propertyUid]: (validation.data as any).errors?.join('; ') || t('properties.error_update') }))
                    return
                }
                if (validation.data?.value !== undefined) {
                    finalValue = validation.data.value
                }
            } catch {
                // Validation endpoint unavailable — proceed with save
            }

            await updatePropertyValueMutation.mutateAsync(propertyUid, { value: finalValue })

            queryClient.invalidateQueries({ queryKey: queryKeys.documentProperties(org, documentUid) })
            setEditingPropertyUid(null)
            setEditingPropertyValue('')
            setPropertyEditErrors({})
            onPropertyUpdated?.()
        } catch (error) {
            const errorMessage = (error as any)?.title || (error as any)?.message || t('properties.error_update')
            setPropertyEditErrors({ [propertyUid]: errorMessage })
        }
    }, [updatePropertyValueMutation, org, documentUid, queryClient, onPropertyUpdated, propertyDefinitions, t])

    // Sibling property list handed to the shared value cell so the ISO 20022 address editor
    // can also write `address_raw` / `geolocation` when those definitions target documents.
    // Today they do not, so the address save writes `address_iso20022` only.
    const propertySiblings: PropertySibling[] = useMemo(() =>
        properties
            .map((p): PropertySibling | null => {
                const pd = (p as any).property_definition
                return pd?.code && pd?.uid
                    ? { code: pd.code, key: pd.uid, value: p.value as unknown }
                    : null
            })
            .filter((s): s is PropertySibling => s !== null),
        [properties]
    )

    const getPropertyDisplayElement = useCallback((value: unknown, propertyUid?: string) => {
        if (value === null || value === undefined) return null

        return (
            <PropertyValueDisplay
                org={org}
                value={value}
                propertyDefinition={propertyUid ? propertyDefinitions.find(pd => pd.uid === propertyUid) : undefined}
                propertyKey={propertyUid || ''}
                siblings={propertySiblings}
                readonly={!canWriteDocuments}
                saveProperty={handleSavePropertyValue}
            />
        )
     
    }, [propertyDefinitions, org, propertySiblings, handleSavePropertyValue, canWriteDocuments])

    const handleDeletePropertyValue = useCallback(async (propertyUid: string) => {
        try {
            await deletePropertyValueMutation.mutateAsync(propertyUid)

            queryClient.invalidateQueries({ queryKey: queryKeys.documentProperties(org, documentUid) })
            onPropertyUpdated?.()
        } catch (error) {
            console.error('Failed to delete property value:', error)
        }
    }, [deletePropertyValueMutation, org, documentUid, queryClient, onPropertyUpdated])

    const handlePropertyKeyDown = useCallback((e: React.KeyboardEvent, propertyUid: string, value: string) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            handleSavePropertyValue(propertyUid, value)
        } else if (e.key === 'Escape') {
            e.preventDefault()
            handleCancelPropertyEdit()
        }
    }, [handleSavePropertyValue, handleCancelPropertyEdit])

    // Add property handlers
    const handlePropertyDefinitionSelect = useCallback((option: ApiSearchableSelectOption) => {
        setNewPropertyDefinitionUid(option.value)
        setNewPropertyValue('')
        setAddPropertyError(null)
    }, [])

    const handleAddProperty = useCallback(async () => {
        if (!newPropertyDefinitionUid) return

        try {
            const selectedPropDef = propertyDefinitionsAll.find(pd => pd.uid === newPropertyDefinitionUid)
            let processedValue: any = newPropertyValue

            // Process value based on property type
            if (selectedPropDef) {
                switch (selectedPropDef.data_type) {
                    case 'boolean':
                        if (!newPropertyValue) {
                            setAddPropertyError(t('properties.error_add'))
                            return
                        }
                        processedValue = newPropertyValue === 'true'
                        break
                    case 'date':
                        if (Array.isArray(newPropertyValue)) {
                            setAddPropertyError('Multiple values not supported for date type')
                            return
                        }
                        if (newPropertyValue && !isNaN(Date.parse(newPropertyValue))) {
                            // Handle datetime-local input values which are in local timezone
                            if (selectedPropDef.properties?.with_time === true) {
                                // For datetime-local, the value is in format YYYY-MM-DDTHH:mm
                                // We need to interpret this as local time and convert to UTC
                                const dateValue = new Date(newPropertyValue)
                                processedValue = dateValue.toISOString()
                            } else {
                                // For date-only, just use the date part
                                const dateValue = new Date(newPropertyValue)
                                processedValue = dateValue.toISOString().split('T')[0]
                            }
                        } else {
                            setAddPropertyError('Please enter a valid date')
                            return
                        }
                        break
                    case 'enum':
                        // For multiple enum values, convert comma-separated string to array
                        if (selectedPropDef.properties?.multiple === true) {
                            processedValue = newPropertyValue ? newPropertyValue.split(',').map(v => v.trim()).filter(v => v) : []
                        } else {
                            processedValue = newPropertyValue
                        }
                        break
                    case 'number':
                        if (newPropertyValue !== '') {
                            processedValue = parseFloat(newPropertyValue)
                            if (isNaN(processedValue)) {
                                setAddPropertyError('Please enter a valid number')
                                return
                            }
                            // Check if integer_only constraint is enabled
                            if (selectedPropDef.properties?.integer_only && !Number.isInteger(processedValue)) {
                                setAddPropertyError('Please enter an integer (whole number)')
                                return
                            }
                            // Check min/max constraints
                            if (typeof selectedPropDef.properties?.min_value === 'number' && processedValue < selectedPropDef.properties.min_value) {
                                setAddPropertyError(`Value must be at least ${selectedPropDef.properties.min_value}`)
                                return
                            }
                            if (typeof selectedPropDef.properties?.max_value === 'number' && processedValue > selectedPropDef.properties.max_value) {
                                setAddPropertyError(`Value must be at most ${selectedPropDef.properties.max_value}`)
                                return
                            }
                        }
                        break
                    case 'text':
                        // Check regex pattern validation
                        if (newPropertyValue && selectedPropDef.properties?.regex_pattern && typeof selectedPropDef.properties.regex_pattern === 'string') {
                            try {
                                const regex = new RegExp(selectedPropDef.properties.regex_pattern)
                                if (!regex.test(newPropertyValue)) {
                                    setAddPropertyError(`Value does not match required pattern: ${selectedPropDef.properties.regex_pattern}`)
                                    return
                                }
                            } catch (regexError) {
                                if (regexError instanceof Error && regexError.message.includes('pattern')) {
                                    setAddPropertyError(regexError.message)
                                    return
                                }
                                setAddPropertyError('Invalid regex pattern in property definition')
                                return
                            }
                        }
                        processedValue = newPropertyValue
                        break
                    default:
                        processedValue = newPropertyValue
                }
            }

            await setPropertyValueMutation.mutateAsync({
                properties: [{
                    property_uid: newPropertyDefinitionUid,
                    value: processedValue
                }]
            })

            queryClient.invalidateQueries({ queryKey: queryKeys.documentProperties(org, documentUid) })
            setNewPropertyDefinitionUid('')
            setNewPropertyValue('')
            setShowAddPropertyForm(false)
            setAddPropertyError(null)
            onPropertyUpdated?.()
        } catch (error) {
            const errorMessage = (error as any)?.title || (error as any)?.message || t('properties.error_add')
            setAddPropertyError(errorMessage)
        }
    }, [setPropertyValueMutation, org, documentUid, newPropertyDefinitionUid, newPropertyValue, queryClient, onPropertyUpdated, propertyDefinitionsAll, t])

    const handleCancelAddProperty = useCallback(() => {
        setNewPropertyDefinitionUid('')
        setNewPropertyValue('')
        setShowAddPropertyForm(false)
        setAddPropertyError(null)
    }, [])

    // Render property value input for editing
    const renderEditPropertyInput = useCallback((propertyDefinitionUid: string, value: string, setValue: (value: string) => void, onKeyDown: (e: React.KeyboardEvent) => void) => (
        <PropertyValueEditInput
            org={org}
            propertyDefinition={getPropertyDefinition(propertyDefinitionUid)}
            value={value}
            onChange={setValue}
            onKeyDown={onKeyDown}
            disabled={updatePropertyValueMutation.isPending}
            hasError={Boolean(propertyEditErrors[propertyDefinitionUid])}
        />
    ), [getPropertyDefinition, propertyEditErrors, updatePropertyValueMutation.isPending, org])

    // Render property value input for add form
    const renderPropertyValueInput = useCallback(() => {
        if (!selectedPropertyDef) return null

        if (selectedPropertyDef.data_type === 'boolean') {
            const semantic = selectedPropertyDef.properties?.semantic as string | undefined
            const options = getBooleanOptions(semantic, tCommon)
            return (
                <select
                    value={newPropertyValue}
                    onChange={(e) => setNewPropertyValue(e.target.value)}
                    className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                >
                    <option value="">{t('properties.select_value')}</option>
                    {options.map(option => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
            )
        }

        if (selectedPropertyDef.data_type === 'date') {
            const withTime = selectedPropertyDef.properties?.with_time === true
            return (
                <input
                    type={withTime ? "datetime-local" : "date"}
                    value={newPropertyValue}
                    onChange={(e) => setNewPropertyValue(e.target.value)}
                    onFocus={(e) => (e.currentTarget as any).showPicker?.()}
                    className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                />
            )
        }

        if (selectedPropertyDef.data_type === 'enum') {
            return <EnumPropertyInput propertyDefinitionUid={selectedPropertyDef.uid} value={newPropertyValue} onChange={setNewPropertyValue} org={org} />
        }

        // For number inputs with integer constraint
        if (selectedPropertyDef.data_type === 'number') {
            return (
                <input
                    type="number"
                    value={newPropertyValue}
                    onChange={(e) => setNewPropertyValue(e.target.value)}
                    step={selectedPropertyDef.properties?.integer_only ? "1" : "any"}
                    min={typeof selectedPropertyDef.properties?.min_value === 'number' ? selectedPropertyDef.properties.min_value : undefined}
                    max={typeof selectedPropertyDef.properties?.max_value === 'number' ? selectedPropertyDef.properties.max_value : undefined}
                    className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    placeholder={`Enter ${selectedPropertyDef.properties?.integer_only ? 'integer' : 'number'}${selectedPropertyDef.properties?.unit ? ` (${selectedPropertyDef.properties.unit})` : ''}...`}
                />
            )
        }

        // For text inputs with regex validation
        if (selectedPropertyDef.data_type === 'text') {
            const hasValidationError = selectedPropertyDef.properties?.regex_pattern && newPropertyValue && (() => {
                try {
                    const regex = new RegExp(selectedPropertyDef.properties.regex_pattern as string)
                    return !regex.test(newPropertyValue)
                } catch {
                    return false
                }
            })()

            return (
                <div>
                    <input
                        type="text"
                        value={newPropertyValue}
                        onChange={(e) => setNewPropertyValue(e.target.value)}
                        className={`flex h-9 w-full rounded-md border ${hasValidationError ? 'border-destructive' : 'border-input'} bg-background px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring`}
                        placeholder={t('properties.enter_text')}
                    />
                    {hasValidationError ? (
                        <p className="text-xs text-destructive mt-1">
                            Pattern: {(selectedPropertyDef.properties?.regex_pattern as string) || ''}
                        </p>
                    ) : null}
                </div>
            )
        }

        // Default input for other types
        return (
            <input
                type="text"
                value={newPropertyValue}
                onChange={(e) => setNewPropertyValue(e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                placeholder={t('properties.enter_value')}
            />
        )
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPropertyDef, newPropertyValue, org, t])

    return (
        <div className="bg-card rounded-lg border border-border p-6">
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-foreground">{t('properties.title')}</h3>
                {canWriteDocuments && (
                    <Button
                        onClick={() => setShowAddPropertyForm(!showAddPropertyForm)}
                        className="bg-primary hover:bg-primary/90 text-primary-foreground px-4 py-2 text-sm"
                        aria-label={t('properties.add_property')}
                        title={t('properties.add_property')}
                        data-testid="document-properties-add-property-btn"
                    >
                        <AppIcon path={mdiPlus} size="md" className="md:mr-1" />
                        <span className="hidden md:inline">{t('properties.add_property')}</span>
                    </Button>
                )}
            </div>

            {/* Add Property Form */}
            {showAddPropertyForm && (
                <div className="mb-6 p-4 bg-muted/50 rounded-lg border border-border">
                    <h4 className="text-md font-medium text-foreground mb-3">{t('properties.add_new_property_title')}</h4>
                    <div className="grid grid-cols-1 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-muted-foreground mb-1">
                                {t('properties.property_definition_label')}
                            </label>
                            <ApiSearchableSelect
                                value={newPropertyDefinitionUid}
                                onValueChange={setNewPropertyDefinitionUid}
                                onOptionSelect={handlePropertyDefinitionSelect}
                                onSearch={searchPropertyDefinitions}
                                placeholder={t('properties.property_definition_placeholder')}
                                searchPlaceholder={t('properties.property_definition_search')}
                                emptyMessage={t('properties.property_definition_empty')}
                                initialOptions={propertyDefinitionInitialOptions}
                                className="w-full"
                                data-testid="property-definition-select"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-muted-foreground mb-1">
                                {t('properties.value_label')}
                            </label>
                            {renderPropertyValueInput()}
                        </div>
                    </div>
                    {addPropertyError && (
                        <div className="mt-3 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
                            <p className="text-sm text-destructive">{addPropertyError}</p>
                        </div>
                    )}
                    <div className="flex gap-2 mt-4">
                        <Button
                            onClick={handleAddProperty}
                            disabled={setPropertyValueMutation.isPending || !newPropertyDefinitionUid}
                            className="bg-primary hover:bg-primary/90 text-primary-foreground px-4 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {setPropertyValueMutation.isPending ? t('properties.adding') : t('properties.add_property')}
                        </Button>
                        <Button
                            onClick={handleCancelAddProperty}
                            disabled={setPropertyValueMutation.isPending}
                            variant="outline"
                            className="px-4 py-2 text-sm"
                        >
                            {t('common:actions.cancel')}
                        </Button>
                    </div>
                </div>
            )}

            {propertiesLoading ? (
                <div className="text-muted-foreground">{t('properties.loading')}</div>
            ) : properties.length === 0 ? (
                <EmptyState variant="inline" icon={mdiTag} title={t('properties.empty')} />
            ) : (
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-border">
                        <thead className="bg-muted/50">
                            <tr>
                                <th className="px-6 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                                    {t('properties.column_property')}
                                </th>
                                <th className="px-6 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                                    {t('properties.column_value')}
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-card divide-y divide-border">
                            {properties.map((property) => {
                                const propertyDef = (property as any).property_definition
                                const propertyDefUid = propertyDef?.uid || ''
                                return (
                                <tr key={propertyDefUid} className="hover:bg-muted/50">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                                        <div>
                                            <div className="flex items-center gap-1">
                                                {getPropertyDefinitionName(propertyDefUid)}
                                                {propertyDef?.flags?.includes('system_managed') && (
                                                    <span title={t('properties.system_managed')}>
                                                        <AppIcon tone="muted" path={mdiLock} size="xs" />
                                                    </span>
                                                )}
                                            </div>
                                            {(() => {
                                                const propDef = propertyDefinitions.find(pd => pd.uid === propertyDefUid)
                                                const constraints = getPropertyConstraints(propDef, tCommon)

                                                return constraints.length > 0 ? (
                                                    <div className="text-xs text-muted-foreground mt-1 font-mono">
                                                        {constraints.join(' • ')}
                                                    </div>
                                                ) : null
                                            })()}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                                        {editingPropertyUid === propertyDefUid ? (
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    {renderEditPropertyInput(
                                                        propertyDefUid,
                                                        editingPropertyValue,
                                                        setEditingPropertyValue,
                                                        (e) => handlePropertyKeyDown(e, propertyDefUid, editingPropertyValue)
                                                    )}
                                                    <Button
                                                        onClick={() => handleSavePropertyValue(propertyDefUid, editingPropertyValue)}
                                                        disabled={updatePropertyValueMutation.isPending}
                                                        className="bg-primary hover:bg-primary/90 text-primary-foreground px-2 py-1 text-sm"
                                                        data-testid="save-property-value"
                                                    >
                                                        {updatePropertyValueMutation.isPending ? '...' : <AppIcon path={mdiCheck} size="xs" />}
                                                    </Button>
                                                    <Button
                                                        onClick={handleCancelPropertyEdit}
                                                        disabled={updatePropertyValueMutation.isPending}
                                                        variant="outline"
                                                        className="px-2 py-1 text-sm"
                                                        data-testid="cancel-property-edit"
                                                    >
                                                        <AppIcon path={mdiClose} size="xs" />
                                                    </Button>
                                                </div>
                                                {propertyEditErrors[propertyDefUid] && (
                                                    <p className="mt-1 text-xs text-destructive">{propertyEditErrors[propertyDefUid]}</p>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="flex items-center justify-between group">
                                                <span
                                                    className={!canWriteDocuments || propertyDef?.flags?.includes('system_managed') || usesDialogEditor(propertyDef, property.value)
                                                        ? 'cursor-default p-1 rounded transition-colors flex-1'
                                                        : 'cursor-pointer hover:bg-muted/50 p-1 rounded transition-colors flex-1'}
                                                    onClick={() => {
                                                        if (!canWriteDocuments) return
                                                        // Object-valued properties are edited through their own dialog
                                                        // (address / JSON) — never seed one into the inline text input.
                                                        if (!propertyDef?.flags?.includes('system_managed') && !usesDialogEditor(propertyDef, property.value)) {
                                                            startEditingProperty({ ...property, property_uid: propertyDefUid })
                                                        }
                                                    }}
                                                    title={propertyDef?.flags?.includes('system_managed') ? t('properties.system_managed') : t('properties.click_to_edit')}
                                                    data-testid="edit-property-value"
                                                >
                                                    {getPropertyDisplayElement(property.value, propertyDefUid) || (propertyDef?.flags?.includes('system_managed') ? 'N/A' : t('properties.click_to_edit'))}
                                                </span>
                                                {canWriteDocuments && !propertyDef?.flags?.includes('system_managed') && (
                                                    <Button
                                                        onClick={() => handleDeletePropertyValue(propertyDefUid)}
                                                        disabled={deletePropertyValueMutation.isPending}
                                                        variant="ghost"
                                                        size="sm"
                                                        className="opacity-0 group-hover:opacity-100 transition-opacity p-1 h-auto text-destructive hover:text-destructive hover:bg-destructive/10"
                                                        title={t('properties.delete_property')}
                                                        data-testid="delete-property-value"
                                                    >
                                                        <AppIcon path={mdiDelete} size="xs" />
                                                    </Button>
                                                )}
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            )})}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    )
}